// Finance build (Workstream E) — cross-loan Loan Payments ledger at
// /portfolio-admin/loan-payments. READ-ONLY display over the v3 payment
// ledger the engine already produces: one row per real recorded servicing
// Payment across all loans, with its waterfall split summary (disbursement
// count + total distributed) and per-payment expected-vs-received income
// variance.
//
// Each row links the payment to the existing per-loan allocation drill-in
// (the Payment Tracking tab's allocation drawer, deep-linked via
// ?payment=) — this surface builds NO new drawer.

import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  EmptyState,
  GlossaryHint,
  PageHeader,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import {
  useLoanPaymentsLedger,
} from "@/hooks/portfolioAdmin/useLoanPayments";
import type {
  LoanPaymentRowDto,
  LoanPaymentsLedgerParams,
} from "@/api/portfolioAdmin/loanPaymentsApi";

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "All statuses" },
  { value: "processed", label: "Processed" },
  // Distribution Gate (B3 payout gate) — a recorded run held for B3 review is
  // listed here (labeled) but excluded from the collected total until approved.
  { value: "pending_review", label: "Pending review" },
  { value: "reversed", label: "Reversed" },
  { value: "draft", label: "Draft" },
  { value: "previewed", label: "Previewed" },
  { value: "superseded", label: "Superseded" },
];

function statusTone(status: string): StatusTone {
  switch (status) {
    case "processed":
      return "ok";
    case "pending_review":
      return "warn";
    case "reversed":
    case "superseded":
      return "danger";
    case "previewed":
      return "info";
    default:
      return "neutral";
  }
}

function formatType(type: string): string {
  return type
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Link a payment row to its per-loan allocation drill-in (the Payment
 *  Tracking tab's existing allocation drawer). */
function allocationHref(row: LoanPaymentRowDto): string | null {
  if (!row.loanId) return null;
  return `/portfolio-admin/loans/${row.loanId}?tab=payment-tracking&payment=${row.paymentId}`;
}

export default function LoanPaymentsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";

  const queryParams = useMemo<LoanPaymentsLedgerParams>(
    () => ({
      status: status || undefined,
      from: from || undefined,
      // The picker emits a bare `YYYY-MM-DD`, which binds server-side to
      // MIDNIGHT, and the filter is `PaymentDate <= to`. So an unwidened upper
      // bound excludes the whole of the day the operator asked for — and this
      // ledger's own rows make that total rather than partial: the Record
      // Payment wizard writes `PaymentDate` at NOON UTC
      // (`calendarDateInputToIso`), so `To = 2026-09-03` dropped EVERY
      // wizard-recorded payment of the 3rd from a money ledger, silently.
      //
      // Five sibling surfaces already widen exactly this way — PortalAccounts,
      // Preleads (staff and portal), GlobalReviewQueue, portal Documents — and
      // CLAUDE.md records the convention. This page was the outlier.
      //
      // 🔴 `from` is deliberately NOT widened, and that asymmetry is the
      // conservative choice rather than an oversight. Widening the UPPER bound
      // can only ADD rows, whatever zone the server binds a bare date in.
      // Widening the lower one is a no-op if the server runs UTC and a
      // lower-bound SHIFT if it does not, and there is no dotnet SDK here to
      // establish which. Which zone should bound both ends is the open ruling
      // in the audit's §7.5; this closes the drop-the-day defect without
      // pre-empting it.
      to: to ? `${to}T23:59:59.999Z` : undefined,
    }),
    [status, from, to],
  );

  const ledger = useLoanPaymentsLedger(queryParams);
  const rows = ledger.data?.rows ?? [];
  const summary = ledger.data?.summary;

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const clearFilters = () => setParams(new URLSearchParams(), { replace: true });

  const hasFilters = !!status || !!from || !!to;

  return (
    <div className="flex flex-col gap-space-5" data-testid="loan-payments-page">
      <PageHeader
        eyebrow="Portfolio administration"
        title="Loan Payments"
        description="Every recorded servicing payment across all loans, with its waterfall split and income variance."
      />

      <div
        className="flex flex-wrap items-end gap-space-3"
        data-testid="loan-payments-filters"
      >
        <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
          Status
          <select
            value={status}
            onChange={(e) => setParam("status", e.target.value)}
            className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx"
            data-testid="loan-payments-status-filter"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setParam("from", e.target.value)}
            className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx"
            data-testid="loan-payments-from-filter"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setParam("to", e.target.value)}
            className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx"
            data-testid="loan-payments-to-filter"
          />
        </label>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-small text-gold-shadow hover:underline"
            data-testid="loan-payments-clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      {summary && (
        <div
          className="flex flex-wrap gap-space-5 rounded-lg border border-border-light bg-white-pure px-space-4 py-space-3"
          data-testid="loan-payments-summary"
        >
          <SummaryStat label="Payments" value={String(summary.count)} />
          <SummaryStat
            label={
              <GlossaryHint term="Collected">Collected (net of reversals)</GlossaryHint>
            }
            value={formatCurrency(summary.totalAmount)}
          />
          <SummaryStat
            label="Total distributed"
            value={formatCurrency(summary.totalDistributed)}
          />
          <SummaryStat
            label="Income variance"
            value={formatCurrency(summary.totalExpectedIncome - summary.totalReceivedIncome)}
          />
        </div>
      )}

      {ledger.isError ? (
        <div
          className="rounded-lg border border-status-error bg-status-error/12 px-space-4 py-space-3 text-small text-status-error"
          data-testid="loan-payments-error"
        >
          Couldn&apos;t load loan payments. Try again.
        </div>
      ) : ledger.isLoading ? (
        <p className="text-small text-gray-mid" data-testid="loan-payments-loading">
          Loading payments…
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No payments"
          description={
            hasFilters
              ? "No payments match the current filters."
              : "Recorded servicing payments will appear here as loans are serviced."
          }
          variant={hasFilters ? "noResults" : "empty"}
          action={hasFilters ? { label: "Clear filters", onClick: clearFilters } : undefined}
        />
      ) : (
        <Table data-testid="loan-payments-table">
          <THead>
            <TR>
              <TH>Loan</TH>
              <TH>Date</TH>
              <TH>Type</TH>
              <TH>Status</TH>
              <TH className="text-right">Amount</TH>
              <TH className="text-right">Distributed</TH>
              <TH className="text-right">Income variance</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => {
              const href = allocationHref(row);
              return (
                <TR key={row.paymentId} data-testid={`loan-payments-row-${row.paymentId}`}>
                  <TD>
                    {row.loanId ? (
                      <Link
                        to={`/portfolio-admin/loans/${row.loanId}`}
                        className="text-gold-shadow hover:underline"
                        data-testid={`loan-payments-loan-link-${row.paymentId}`}
                      >
                        {row.loanNumber ?? "View loan"}
                      </Link>
                    ) : (
                      <span className="text-gray-mid">—</span>
                    )}
                  </TD>
                  {/* Explicit `null` = "do NOT resolve the viewer's zone here",
                      said out loud rather than by omission. `Payment.PaymentDate`
                      is MIXED at the data layer: RightSize/Closing/VfiInflow widen
                      a `DateOnly` to UTC MIDNIGHT, the Record Payment wizard writes
                      the operator's picked day at NOON UTC via
                      `calendarDateInputToIso`, and the adjustment/reversal paths
                      write `UtcNow`. So some rows are wall-calendar and some are
                      instants, and neither renderer is right for all of them —
                      threading the viewer's zone would move the midnight-UTC rows a
                      day BACKWARD for anyone west of UTC. Same call as `paidAt` in
                      `VendorVfiLedgerTab`; closing it needs a server-side split. */}
                  <TD>{formatDateInTz(row.paymentDate, null)}</TD>
                  <TD>
                    {href ? (
                      <Link
                        to={href}
                        className="text-onyx hover:text-gold-shadow hover:underline"
                        data-testid={`loan-payments-allocation-link-${row.paymentId}`}
                      >
                        {formatType(row.paymentType)}
                      </Link>
                    ) : (
                      formatType(row.paymentType)
                    )}
                    {row.paidAheadFromPaymentId && row.loanId && (
                      <Link
                        to={`/portfolio-admin/loans/${row.loanId}?tab=payment-tracking&payment=${row.paidAheadFromPaymentId}`}
                        className="block text-caption text-gray-mid hover:text-gold-shadow hover:underline"
                        data-testid={`loan-payments-pay-ahead-link-${row.paymentId}`}
                      >
                        ↑ Pay-ahead — from an earlier payment
                      </Link>
                    )}
                  </TD>
                  <TD>
                    <StatusBadge tone={statusTone(row.status)}>
                      {formatType(row.status)}
                    </StatusBadge>
                  </TD>
                  <TD className="text-right tabular-nums">{formatCurrency(row.amount)}</TD>
                  <TD className="text-right tabular-nums">
                    {formatCurrency(row.totalDistributed)}
                    <span className="ml-space-1 text-caption text-gray-mid">
                      ({row.disbursementCount})
                    </span>
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatCurrency(row.incomeVariance)}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
    </div>
  );
}

function SummaryStat({ label, value }: { label: ReactNode; value: string }) {
  return (
    <div className="flex flex-col gap-space-1">
      <span className="text-caption uppercase tracking-label text-gray-mid">{label}</span>
      <span className="font-display text-h3 font-bold text-onyx">{value}</span>
    </div>
  );
}
